/**
 * HOME — WHAT SCOUT IS RIGHT NOW, AND WHAT IT STILL CANNOT DO.
 *
 * THE ONE RULE THIS SCREEN IS BUILT AROUND, AND IT IS THE BRIEF'S: SERVICE
 * HEALTH IS NOT SCIENTIFIC CAPABILITY. A Worker answering HTTP 200 proves that
 * a Worker answered. It says nothing about whether a reasoning provider is
 * configured, whether any search provider has a credential, whether an
 * experiment runner is attached, or whether anything has ever been researched.
 * So the two are rendered as two separate panels, in that order, and NOTHING
 * anywhere on this screen combines them into one figure.
 *
 * THERE IS NO INTELLIGENCE SCORE, AND THERE IS NO HEALTH SCORE EITHER. Both
 * would be a single number standing in for several facts that fail
 * independently, and a single number is exactly what a reader cannot check.
 * Every line below is a fact with a named source, and the ones that are UNKNOWN
 * say so rather than being filled in.
 *
 * FOUR READS, FOUR INDEPENDENT FAILURES. The liveness probe, the capability
 * registry, what is running, and what needs the Owner. They are loaded
 * separately and each reports its own failure, because a screen that showed one
 * error for four questions would hide which of them is broken — and "Scout is
 * down" and "the research list is down" are different facts that lead to
 * different actions.
 */

import { useEffect, useMemo } from 'preact/hooks';
import { createLoadable } from '../resource';
import { probeLiveness, readGateway, type LivenessResult } from '../gateway';
import { Failure } from './failure';
import { navigate } from '../router';
import { pathForDestination } from '../paths';

interface Facilit { available: string[]; unavailable: string[] }

interface Disclosure {
	version: string;
	generatedAt: string;
	faculties: { search: Facilit; reasoning: { available: boolean } };
	authority: string;
}

interface Capability {
	key: string;
	label: string;
	status: string;
	producedBy: string | null;
	reason: string | null;
	runtimeLevel?: string;
}

interface BuildIdentity {
	gatewayVersion: string;
	workerRevisionId: string | null;
	workerRevisionTag: string | null;
	revisionSource: string;
	revisionNote: string;
	serviceId: string | null;
}

interface Capabilities extends Disclosure {
	searchDescription: string;
	cognition: Capability[];
	build: BuildIdentity;
	resourceAccount: unknown;
}

interface ResearchRun {
	runId: string;
	state: string;
	requestedMs: number;
	deadlineAt: string;
	startedAt: string;
	endedAt: string | null;
	endReason: string | null;
	segmentsRun: number;
	unrecoverableSegments: number;
	drySegments: number;
	cancelRequested: boolean;
}

interface LabStatus {
	agent: { presence: string; agentId: string | null; lastSeenAt: number | null };
	availability: string;
	oldestQueued: unknown | null;
}

interface InboxItem {
	notificationId: string;
	class: string;
	title: string;
	createdAt: string;
	readAt: string | null;
}

interface Inbox {
	notifications: InboxItem[];
	totalCount: number;
	unreadCount: number;
}

/** HOW MANY OF THE DECLARED CAPABILITIES SIT AT EACH STATUS. A count of
 *  DECLARATIONS, never of achievements — the label says so where it is shown. */
function summarise(cognition: Capability[]) {
	return {
		total: cognition.length,
		reachable: cognition.filter((entry) => entry.status === 'AVAILABLE_AND_REACHABLE').length,
		noProducer: cognition.filter((entry) => entry.status === 'AVAILABLE_BUT_NO_RUNTIME_PRODUCER')
			.length,
		notReachableHere: cognition.filter((entry) => entry.status === 'DECLARED_NOT_REACHABLE_HERE')
			.length,
		configuredUnverified: cognition.filter(
			(entry) => entry.status === 'CONFIGURED_NOT_VERIFIED_HERE',
		).length,
		/** The deployment rung, for the two capabilities whose availability is
		 *  genuinely a deployment question. Empty when the deployment reported
		 *  nothing, which is not the same as "everything is fine". */
		rungs: cognition.filter((entry) => entry.runtimeLevel !== undefined),
	};
}

/** A number with the thing it counts named beside it. Never a bare figure: a
 *  lone "0" on a status screen reads as a failure and a lone "29" reads as an
 *  achievement, and neither is what either number means. */
function Figure({ value, of, label }: { value: number; of?: number; label: string }) {
	return (
		<p class="figure">
			<span class="count">{value}</span>
			{of !== undefined && <span class="muted"> of {of}</span>}
			<span class="figure-label"> {label}</span>
		</p>
	);
}

export function HomeView() {
	// THE PROBE ALWAYS ANSWERS, SO ITS LOADABLE CANNOT FAIL. "Nothing is
	// answering" is a RESULT here, not an error — that is the whole reason the
	// probe exists — so it is wrapped as a successful read of an answer that
	// happens to be bad news. Treating it as a failure would put it in the same
	// shape as a refused read, and the difference between "the deployment is
	// down" and "this session was refused" is exactly what this screen must keep
	// legible.
	const liveness = useMemo(
		() => createLoadable<LivenessResult>(async () => ({ ok: true, data: await probeLiveness() })),
		[],
	);
	const capabilities = useMemo(
		() => createLoadable<Capabilities>(() => readGateway<Capabilities>('/api/scout/gateway/capabilities')),
		[],
	);
	const runs = useMemo(
		() =>
			createLoadable<{ runs: ResearchRun[] }>(() =>
				readGateway<{ runs: ResearchRun[] }>('/api/scout/gateway/research/runs'),
			),
		[],
	);
	const inbox = useMemo(
		() =>
			createLoadable<Inbox>(() =>
				readGateway<Inbox>('/api/scout/gateway/notifications?unreadOnly=true&limit=10'),
			),
		[],
	);
	const lab = useMemo(
		() => createLoadable<LabStatus>(() => readGateway<LabStatus>('/api/scout/gateway/lab/status')),
		[],
	);

	// FIVE LOADS, EACH ITS OWN. Started together because they are independent,
	// and NOT awaited together: a slow research list must not hold the liveness
	// answer, which is the one a stuck Owner needs first.
	useEffect(() => {
		void liveness.load();
		void capabilities.load();
		void runs.load();
		void inbox.load();
		void lab.load();
	}, [liveness, capabilities, runs, inbox, lab]);

	const live = liveness.state.value;
	const caps = capabilities.state.value;
	const runState = runs.state.value;
	const inboxState = inbox.state.value;
	const labState = lab.state.value;

	const running = (runState.data?.runs ?? []).filter((run) => run.state === 'RUNNING');
	const summary = caps.data === null ? null : summarise(caps.data.cognition);

	return (
		<section class="view home-view" aria-labelledby="home-heading">
			<header class="view-head">
				<h1 id="home-heading">System overview</h1>
				<p class="view-blurb">
					Whether Scout is answering, what it can actually do in this deployment, what is running
					now, and what is waiting for you.
				</p>
			</header>

			{/* ---------------------------------------------------- 1. IS IT UP */}
			<section class="panel" aria-labelledby="liveness-heading">
				<h2 id="liveness-heading">Is Scout answering?</h2>
				{live.status === 'loading' && (
					<p class="notice" role="status">
						Asking Scout’s liveness route…
					</p>
				)}
				{live.status === 'ready' && live.data !== null && (
					<>
						<p class="verdict">
							{live.data.state === 'answering' && (
								<>
									<strong>Yes.</strong> A Worker answered <code>/api/health</code>
									{live.data.product !== null && <> as <code>{live.data.product}</code></>} at{' '}
									<time dateTime={live.data.at}>{live.data.at}</time>.
								</>
							)}
							{live.data.state === 'unreachable' && (
								<>
									<strong>No.</strong> Nothing answered: {live.data.detail}.
								</>
							)}
							{live.data.state === 'unexpected' && (
								<>
									<strong>Not in a way this screen trusts.</strong> Something replied, but{' '}
									{live.data.detail}.
								</>
							)}
						</p>
						<p class="muted small">
							<strong>This proves a Worker is running and nothing else.</strong> It is asked
							without a session, which is why it can answer when everything below cannot — and
							it is why a “yes” here must never be read as “Scout is working”. Whether Scout can
							reason, search or research is answered by the panels below, each from its own
							source.
						</p>
					</>
				)}
			</section>

			{/* --------------------------------------------- 2. WHICH BUILD */}
			{caps.status === 'failed' && caps.error !== null && (
				<Failure
					error={caps.error}
					what="what this deployment is"
					onRetry={() => void capabilities.load()}
				/>
			)}
			{caps.status === 'ready' && caps.data !== null && (
				<>
					<section class="panel" aria-labelledby="build-heading">
						<h2 id="build-heading">Which build is answering</h2>
						<dl class="facts">
							<div>
								<dt>Payload schema</dt>
								<dd>
									<code>{caps.data.build.gatewayVersion}</code>
									<span class="muted small">
										{' '}
										— the shape of these responses, not a build number
									</span>
								</dd>
							</div>
							<div>
								<dt>Worker revision</dt>
								<dd>
									{caps.data.build.workerRevisionId === null ? (
										<span class="muted">not stated by this deployment</span>
									) : (
										<code>
											{caps.data.build.workerRevisionId}
											{caps.data.build.workerRevisionTag !== null &&
												` (${caps.data.build.workerRevisionTag})`}
										</code>
									)}
								</dd>
							</div>
							{caps.data.build.serviceId !== null && (
								<div>
									<dt>Deployment name</dt>
									<dd>
										<code>{caps.data.build.serviceId}</code>
										<span class="muted small"> — a name, not a version</span>
									</dd>
								</div>
							)}
							<div>
								<dt>Answered at</dt>
								<dd>
									<time dateTime={caps.data.generatedAt}>{caps.data.generatedAt}</time>
								</dd>
							</div>
						</dl>
						{/* THE ABSENCE IS EXPLAINED, NOT LEFT AS A DASH. "Unknown" on its
						    own reads like a bug; naming the binding makes it a thing an
						    operator can go and provision. */}
						<p class={caps.data.build.workerRevisionId === null ? 'notice small' : 'muted small'}>
							{caps.data.build.revisionNote}
						</p>
					</section>

					{/* ------------------------------------- 3. WHAT IT CAN DO HERE */}
					<section class="panel" aria-labelledby="capability-heading">
						<h2 id="capability-heading">What it can actually do here</h2>
						<p class="muted small">
							These are counts of what this build <em>declares</em> and how far this deployment
							got with each — never of what Scout has achieved or learned. Ask{' '}
							<a
								href={pathForDestination('status')}
								onClick={(event) => {
									event.preventDefault();
									navigate({ destination: 'status', section: null, recordId: null });
								}}
							>
								Status
							</a>{' '}
							for every one of them by name.
						</p>
						{summary !== null && (
							<>
								<Figure value={summary.reachable} of={summary.total} label="declared and reachable in this build" />
								{summary.noProducer > 0 && (
									<Figure value={summary.noProducer} label="declared with nothing in this build that produces them" />
								)}
								{summary.notReachableHere > 0 && (
									<Figure
										value={summary.notReachableHere}
										label="declared, and this deployment was never given the binding, credential or host they need"
									/>
								)}
								{summary.configuredUnverified > 0 && (
									<Figure value={summary.configuredUnverified} label="wired here, and nothing has demonstrated it works" />
								)}
								{summary.rungs.length > 0 && (
									<>
										<h3 class="subhead">How far this deployment got</h3>
										<p class="muted small">
											Only capabilities whose availability is genuinely a deployment question
											are answered here. A capability absent from this list is one this
											deployment made no claim about — not one that works.
										</p>
										<ul class="rungs">
											{summary.rungs.map((entry) => (
												<li key={entry.key}>
													<code>{entry.key}</code> — <code>{entry.runtimeLevel}</code>
													{entry.reason !== null && <span class="muted"> — {entry.reason}</span>}
												</li>
											))}
										</ul>
									</>
								)}
							</>
						)}

						<h3 class="subhead">The two faculties everything model-mediated depends on</h3>
						<dl class="facts">
							<div>
								<dt>Reasoning provider</dt>
								<dd>
									{caps.data.faculties.reasoning.available ? (
										'configured for this deployment'
									) : (
										<>
											<strong>not configured here.</strong>{' '}
											<span class="muted">
												every model-mediated path refuses by name, and an answer that
												looks reasoned may still have been read off Scout’s memory
											</span>
										</>
									)}
								</dd>
							</div>
							<div>
								<dt>Search providers</dt>
								<dd>
									{caps.data.faculties.search.available.length > 0 ? (
										<>
											{caps.data.faculties.search.available.join(', ')}
											{caps.data.faculties.search.unavailable.length > 0 && (
												<span class="muted">
													{' '}
													— declared here without a credential:{' '}
													{caps.data.faculties.search.unavailable.join(', ')}
												</span>
											)}
										</>
									) : (
										<span class="muted">
											none configured here
											{caps.data.faculties.search.unavailable.length > 0 &&
												` (${caps.data.faculties.search.unavailable.join(', ')} declared, no credential)`}
										</span>
									)}
								</dd>
							</div>
							<div>
								<dt>Research infrastructure</dt>
								<dd>
									{labState.status === 'ready' && labState.data !== null ? (
										<>
											experiment agent:{' '}
											<code>{labState.data.agent.presence}</code>
											{labState.data.agent.lastSeenAt !== null && (
												<span class="muted">
													{' '}
													— last seen{' '}
													<time dateTime={new Date(labState.data.agent.lastSeenAt).toISOString()}>
														{new Date(labState.data.agent.lastSeenAt).toISOString()}
													</time>
												</span>
											)}
										</>
									) : labState.status === 'failed' && labState.error !== null ? (
										<span class="muted">
											not established: <code>{labState.error.name}</code>
										</span>
									) : (
										<span class="muted">reading…</span>
									)}
								</dd>
							</div>
						</dl>
						{labState.status === 'ready' && labState.data !== null && (
							<p class="muted small">{labState.data.availability}</p>
						)}
						<p class="authority">{caps.data.authority}</p>
					</section>
				</>
			)}

			{/* ------------------------------------------------- 4. RUNNING NOW */}
			<section class="panel" aria-labelledby="running-heading">
				<h2 id="running-heading">What is running now</h2>
				{runState.status === 'loading' && (
					<p class="notice" role="status">
						Reading Scout’s research windows…
					</p>
				)}
				{runState.status === 'failed' && runState.error !== null && (
					<Failure
						error={runState.error}
						what="the research windows"
						onRetry={() => void runs.load()}
					/>
				)}
				{runState.status === 'ready' && runState.data !== null && (
					<>
						{running.length === 0 ? (
							<p>
								<strong>No research window is open.</strong>{' '}
								<span class="muted">
									Scout researches when the Owner authorizes a window or asks it to; between
									those it is not doing research, and this screen will not imply otherwise.
								</span>
							</p>
						) : (
							<ul class="records">
								{running.map((run) => (
									<li key={run.runId} class="record">
										<div class="record-head">
											<code>{run.runId}</code>
											<span class="tag tag-live">{run.state}</span>
										</div>
										<p class="muted small">
											authorized for {Math.round(run.requestedMs / 60_000)} min · opened{' '}
											<time dateTime={run.startedAt}>{run.startedAt}</time> · ends{' '}
											<time dateTime={run.deadlineAt}>{run.deadlineAt}</time>
										</p>
										<p class="muted small">
											{run.segmentsRun} segment(s) run · {run.unrecoverableSegments} could not
											start · {run.drySegments} found nothing
											{run.cancelRequested && ' · a stop has been asked for'}
										</p>
									</li>
								))}
							</ul>
						)}
						<p class="muted small">
							Recent windows, running and ended, are on{' '}
							<a
								href={pathForDestination('history')}
								onClick={(event) => {
									event.preventDefault();
									navigate({ destination: 'history', section: null, recordId: null });
								}}
							>
								History
							</a>
							.
						</p>
					</>
				)}
			</section>

			{/* ------------------------------------------------ 5. NEEDS YOU */}
			<section class="panel" aria-labelledby="attention-heading">
				<h2 id="attention-heading">Waiting for you</h2>
				{inboxState.status === 'loading' && (
					<p class="notice" role="status">
						Reading the inbox…
					</p>
				)}
				{inboxState.status === 'failed' && inboxState.error !== null && (
					<Failure error={inboxState.error} what="the inbox" onRetry={() => void inbox.load()} />
				)}
				{inboxState.status === 'ready' && inboxState.data !== null && (
					<>
						{inboxState.data.totalCount === 0 ? (
							// AN EMPTY INBOX IS STATED AS EMPTY, WITH THE REASON IT CAN BE
							// EMPTY. "No notifications" and "notifications are broken" look
							// identical otherwise, and this product raises none of some
							// declared classes because nothing produces them.
							<p>
								<strong>Nothing has been recorded in the inbox.</strong>{' '}
								<span class="muted">
									That is a fact about this deployment’s producers, not a promise that
									nothing happened — most of what Scout does is not notified, and some
									declared notification classes have no producer at all. See{' '}
									<a
										href={pathForDestination('notifications')}
										onClick={(event) => {
											event.preventDefault();
											navigate({
												destination: 'notifications',
												section: null,
												recordId: null,
											});
										}}
									>
										Notifications
									</a>{' '}
									for which.
								</span>
							</p>
						) : (
							<>
								<Figure
									value={inboxState.data.unreadCount}
									of={inboxState.data.totalCount}
									label="recorded notification(s) you have not read"
								/>
								<ul class="records">
									{inboxState.data.notifications.slice(0, 5).map((item) => (
										<li key={item.notificationId} class="record">
											<div class="record-head">
												<strong>{item.title}</strong>
												<span class="tag">{item.class}</span>
											</div>
											<p class="muted small">
												<time dateTime={item.createdAt}>{item.createdAt}</time>
											</p>
										</li>
									))}
								</ul>
								<p class="muted small">
									<a
										href={pathForDestination('notifications')}
										onClick={(event) => {
											event.preventDefault();
											navigate({
												destination: 'notifications',
												section: null,
												recordId: null,
											});
										}}
									>
										Open the inbox
									</a>{' '}
									to read them and mark them read.
								</p>
							</>
						)}
					</>
				)}
			</section>

			<section class="panel not-known" aria-labelledby="home-not-known">
				<h2 id="home-not-known">What is not known here</h2>
				<ul>
					<li>
						<strong>This screen holds no combined score, deliberately.</strong> Reachability,
						reasoning, search and research infrastructure fail independently, and a single
						number over them would be a figure with nothing behind it.
					</li>
					<li>
						<strong>A capability being reachable is not evidence it works.</strong> The rungs
						above say how far the deployment got — declared, configured, reachable, exercised —
						and only “exercised” means something produced a record here.
					</li>
					<li>
						<strong>Nothing here refreshes itself.</strong> Every panel is the answer to one read
						at one moment.
					</li>
				</ul>
			</section>
		</section>
	);
}
